import { Camera, OrthographicCamera, PerspectiveCamera, Vector3 } from 'three';
import { TargetConfig } from '../api/models';
import { ColorScale } from '../state/sceneStore';
import { getColorFromScale, COLOR_SCALE_LABELS } from './colorScale';

export interface HeatmapLegendInfo {
  visible: boolean;
  min: number;
  max: number;
  colorScale: ColorScale;
}

export interface ExportImageOptions {
  sceneCanvas: HTMLCanvasElement;
  camera: Camera;
  useOrthographic: boolean;
  projectName?: string;
  targetConfig: TargetConfig;
  heatmap?: HeatmapLegendInfo;
  filename?: string;
}

const NICE_LENGTHS_M = [
  0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000,
];

// Overlay sizing scales modestly with the rendered canvas dimensions so that
// text remains legible at high resolutions without dominating small viewports.
// Uses a sublinear, capped scale factor: a 900px canvas renders 1x, a 2000px
// canvas roughly 1.4x, and very large canvases never exceed 1.5x base sizes.
export function computeOverlayMetrics(width: number, height: number) {
  const longSide = Math.max(width, height);
  const scale = Math.max(1, Math.min(1.5, Math.sqrt(longSide / 900)));
  const round = (v: number) => Math.round(v);

  return {
    footerHeight: round(56 * scale),
    legendPanelWidth: round(96 * scale),
    legendStripWidth: round(18 * scale),
    legendStripMaxHeight: round(260 * scale),
    legendStripMinHeight: round(130 * scale),
    fontFooterTitle: round(13 * scale),
    fontFooterMeta: round(11 * scale),
    fontFooterTarget: round(12 * scale),
    fontLegendTitle: round(12 * scale),
    fontLegendValue: round(11 * scale),
    fontScaleLabel: round(13 * scale),
    scaleBarThickness: Math.max(3, round(4 * scale)),
    padding: round(12 * scale),
    panelPadX: round(14 * scale),
    panelPadY: round(8 * scale),
  };
}

export function pickNiceLength(targetMeters: number): number {
  if (!isFinite(targetMeters) || targetMeters <= 0) return NICE_LENGTHS_M[0];
  let best = NICE_LENGTHS_M[0];
  for (const v of NICE_LENGTHS_M) {
    if (v <= targetMeters) best = v;
  }
  // If target exceeds the largest entry, return the largest.
  if (targetMeters > NICE_LENGTHS_M[NICE_LENGTHS_M.length - 1]) {
    best = NICE_LENGTHS_M[NICE_LENGTHS_M.length - 1];
  }
  return best;
}

export function computePxPerMeter(
  camera: Camera,
  useOrthographic: boolean,
  viewportWidth: number,
  viewportHeight: number,
): number {
  if (useOrthographic) {
    const ortho = camera as OrthographicCamera;
    // The orthographic camera in this app is set up with explicit frustum bounds
    // (left/right/top/bottom) AND a `zoom` factor, so the visible world width is
    // (right - left) / zoom. Pixels per meter is therefore the rendered width
    // divided by that visible-world width.
    const zoom = typeof ortho.zoom === 'number' && ortho.zoom > 0 ? ortho.zoom : 1;
    const frustumWidth = (ortho.right ?? 0) - (ortho.left ?? 0);
    if (frustumWidth > 0 && isFinite(frustumWidth) && viewportWidth > 0) {
      return (viewportWidth * zoom) / frustumWidth;
    }
    // If the frustum bounds are missing (e.g. a default-constructed camera), fall
    // back to using viewport height with the vertical bounds, otherwise to zoom.
    const frustumHeight = (ortho.top ?? 0) - (ortho.bottom ?? 0);
    if (frustumHeight > 0 && isFinite(frustumHeight) && viewportHeight > 0) {
      return (viewportHeight * zoom) / frustumHeight;
    }
    return zoom;
  }

  const persp = camera as PerspectiveCamera;
  // Use the distance along the camera's forward direction to the z=0 ground plane.
  // This matches where the heatmap and most analytical content lives.
  let distance = camera.position.length();
  try {
    const forward = new Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    if (Math.abs(forward.z) > 1e-6) {
      const t = -camera.position.z / forward.z;
      if (t > 0 && isFinite(t)) {
        distance = t; // forward is unit-length, so t is the distance along the ray
      }
    }
  } catch {
    // If quaternion math fails, fall back to position length.
  }
  if (!isFinite(distance) || distance <= 0) distance = 1;

  const fov = typeof persp.fov === 'number' && persp.fov > 0 ? persp.fov : 50;
  const fovRad = (fov * Math.PI) / 180;
  // Vertical fov drives the projection; for a uniformly-scaled rendering, the
  // horizontal pixels-per-meter equals the vertical, so we use viewportHeight.
  return viewportHeight / 2 / (Math.tan(fovRad / 2) * distance);
}

function formatLength(m: number): string {
  if (m >= 1) return `${Number.isInteger(m) ? m.toFixed(0) : m.toFixed(1)} m`;
  return `${(m * 100).toFixed(0)} cm`;
}

export function formatTargetConfig(target: TargetConfig): string {
  const w = target.width;
  const h = target.height;
  const d = target.depth;
  const off = target.groundOffset;
  if (target.shape === 'cylinder') {
    return `Target: cylinder \u00D8 ${w.toFixed(2)} m \u00D7 ${h.toFixed(2)} m, ground offset ${off.toFixed(2)} m`;
  }
  return `Target: cuboid ${w.toFixed(2)} \u00D7 ${d.toFixed(2)} \u00D7 ${h.toFixed(2)} m, ground offset ${off.toFixed(2)} m`;
}

function formatHeatmapValue(v: number): string {
  if (!isFinite(v)) return '\u2014';
  const abs = Math.abs(v);
  if (abs >= 100) return v.toFixed(0);
  if (abs >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

function nowTimestampForFilename(): string {
  const d = new Date();
  return d.toISOString().replace(/[:T]/g, '-').replace(/\..+/, '');
}

type OverlayMetrics = ReturnType<typeof computeOverlayMetrics>;

function drawScaleBar(
  ctx: CanvasRenderingContext2D,
  pxPerMeter: number,
  sceneWidth: number,
  sceneHeight: number,
  metrics: OverlayMetrics,
): void {
  if (!isFinite(pxPerMeter) || pxPerMeter <= 0) return;

  const targetPx = sceneWidth * 0.15;
  const targetMeters = targetPx / pxPerMeter;
  const niceMeters = pickNiceLength(targetMeters);
  const barPx = niceMeters * pxPerMeter;
  if (!isFinite(barPx) || barPx <= 0) return;

  const padding = metrics.padding;
  const panelPadX = metrics.panelPadX;
  const panelPadY = metrics.panelPadY;
  const labelLineHeight = metrics.fontScaleLabel + 4;
  const tickHeight = metrics.scaleBarThickness * 3;
  const panelW = barPx + panelPadX * 2;
  const panelH = panelPadY * 2 + tickHeight + labelLineHeight;

  const panelX = padding;
  const panelY = sceneHeight - padding - panelH;

  ctx.save();
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.strokeStyle = 'rgba(0,0,0,0.5)';
  ctx.lineWidth = 1;
  ctx.fillRect(panelX, panelY, panelW, panelH);
  ctx.strokeRect(panelX, panelY, panelW, panelH);

  const barX = panelX + panelPadX;
  const barY = panelY + panelPadY + Math.round(tickHeight / 2);
  const tickWidth = Math.max(2, Math.round(metrics.scaleBarThickness * 0.6));

  ctx.fillStyle = '#000';
  ctx.fillRect(barX, barY, barPx, metrics.scaleBarThickness);
  ctx.fillRect(barX, barY - tickHeight + metrics.scaleBarThickness, tickWidth, tickHeight);
  ctx.fillRect(barX + barPx - tickWidth, barY - tickHeight + metrics.scaleBarThickness, tickWidth, tickHeight);

  ctx.font = `bold ${metrics.fontScaleLabel}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const labelY = barY + metrics.scaleBarThickness + 4;
  ctx.fillText(formatLength(niceMeters), barX + barPx / 2, labelY);

  ctx.restore();
}

function drawHeatmapLegend(
  ctx: CanvasRenderingContext2D,
  heatmap: HeatmapLegendInfo,
  sceneWidth: number,
  sceneHeight: number,
  metrics: OverlayMetrics,
): void {
  const padding = metrics.padding;
  const stripWidth = metrics.legendStripWidth;
  const titleHeight = metrics.fontLegendTitle + 12;
  const valueLineHeight = metrics.fontLegendValue + 6;

  const stripHeight = Math.max(
    metrics.legendStripMinHeight,
    Math.min(metrics.legendStripMaxHeight, sceneHeight - 2 * padding - titleHeight - valueLineHeight),
  );
  const totalWidth = metrics.legendPanelWidth;
  const totalHeight = stripHeight + titleHeight + valueLineHeight;

  const panelX = sceneWidth - totalWidth - padding;
  const panelY = padding;

  ctx.save();
  ctx.fillStyle = 'rgba(15,23,42,0.82)';
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 1;
  ctx.fillRect(panelX, panelY, totalWidth, totalHeight);
  ctx.strokeRect(panelX, panelY, totalWidth, totalHeight);

  ctx.fillStyle = '#e2e8f0';
  ctx.font = `bold ${metrics.fontLegendTitle}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const titleLabel = COLOR_SCALE_LABELS[heatmap.colorScale] ?? 'Color Scale';
  ctx.fillText(titleLabel, panelX + totalWidth / 2, panelY + 8);

  const stripX = panelX + Math.round((totalWidth - stripWidth) / 2) - 12;
  const stripY = panelY + titleHeight;

  // Draw vertical gradient (top = max, bottom = min) one pixel at a time.
  const steps = stripHeight;
  for (let i = 0; i < steps; i++) {
    const normalized = 1 - i / Math.max(1, steps - 1);
    const { r, g, b } = getColorFromScale(normalized, heatmap.colorScale);
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.fillRect(stripX, stripY + i, stripWidth, 1);
  }
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.strokeRect(stripX, stripY, stripWidth, stripHeight);

  ctx.fillStyle = '#e2e8f0';
  ctx.font = `${metrics.fontLegendValue}px sans-serif`;
  ctx.textAlign = 'left';
  const labelX = stripX + stripWidth + 6;
  ctx.textBaseline = 'top';
  ctx.fillText(formatHeatmapValue(heatmap.max), labelX, stripY);
  ctx.textBaseline = 'bottom';
  ctx.fillText(formatHeatmapValue(heatmap.min), labelX, stripY + stripHeight);

  ctx.restore();
}

function drawFooter(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  canvasHeight: number,
  projectName: string | undefined,
  targetConfigText: string | null,
  timestamp: string,
  metrics: OverlayMetrics,
): void {
  const footerHeight = metrics.footerHeight;
  const y = canvasHeight - footerHeight;
  ctx.save();
  ctx.fillStyle = 'rgba(15,23,42,0.97)';
  ctx.fillRect(0, y, canvasWidth, footerHeight);

  const padX = metrics.padding;
  ctx.fillStyle = '#e2e8f0';
  ctx.textBaseline = 'middle';
  ctx.font = `bold ${metrics.fontFooterTitle}px sans-serif`;
  ctx.textAlign = 'left';
  ctx.fillText(projectName || 'Untitled project', padX, y + footerHeight / 2 - metrics.fontFooterMeta);

  ctx.font = `${metrics.fontFooterMeta}px sans-serif`;
  ctx.fillStyle = '#94a3b8';
  ctx.fillText(timestamp, padX, y + footerHeight / 2 + metrics.fontFooterTitle * 0.7);

  if (targetConfigText) {
    ctx.font = `${metrics.fontFooterTarget}px sans-serif`;
    ctx.fillStyle = '#cbd5e1';
    ctx.textAlign = 'right';
    const maxWidth = canvasWidth * 0.6;
    wrapText(
      ctx,
      targetConfigText,
      canvasWidth - padX,
      y + footerHeight / 2,
      maxWidth,
      metrics.fontFooterTarget + 4,
      'right',
    );
  }

  ctx.restore();
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  align: 'left' | 'right' | 'center',
): void {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const w of words) {
    const test = current ? current + ' ' + w : w;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  const totalHeight = lines.length * lineHeight;
  let startY = y - totalHeight / 2 + lineHeight / 2;
  ctx.textBaseline = 'middle';
  ctx.textAlign = align;
  for (const line of lines) {
    ctx.fillText(line, x, startY);
    startY += lineHeight;
  }
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function exportSceneImage(opts: ExportImageOptions): Promise<void> {
  const { sceneCanvas, camera, useOrthographic, projectName, targetConfig, heatmap } = opts;
  if (!sceneCanvas) throw new Error('sceneCanvas is required');

  const srcWidth = sceneCanvas.width;
  const srcHeight = sceneCanvas.height;
  if (!srcWidth || !srcHeight) throw new Error('sceneCanvas has zero size');

  const metrics = computeOverlayMetrics(srcWidth, srcHeight);
  const outputWidth = srcWidth;
  const outputHeight = srcHeight + metrics.footerHeight;

  const out = document.createElement('canvas');
  out.width = outputWidth;
  out.height = outputHeight;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('Failed to get 2D context');

  ctx.fillStyle = '#0f172a';
  ctx.fillRect(0, 0, outputWidth, outputHeight);

  ctx.drawImage(sceneCanvas, 0, 0, srcWidth, srcHeight);

  if (heatmap?.visible) {
    drawHeatmapLegend(ctx, heatmap, srcWidth, srcHeight, metrics);
  }

  // Only render the scale bar in orthographic (top-down) views; in 3D
  // perspective the on-screen scale varies with depth so a single bar would
  // be misleading rather than helpful.
  if (useOrthographic) {
    const pxPerMeter = computePxPerMeter(camera, useOrthographic, srcWidth, srcHeight);
    drawScaleBar(ctx, pxPerMeter, srcWidth, srcHeight, metrics);
  }

  // Target configuration only matters when interpreting the heatmap, so only
  // include it in the footer when the heatmap is being shown.
  const targetText = heatmap?.visible ? formatTargetConfig(targetConfig) : null;
  const isoTimestamp = new Date().toISOString().replace('T', ' ').replace(/\..+/, ' UTC');
  drawFooter(ctx, outputWidth, outputHeight, projectName, targetText, isoTimestamp, metrics);

  await new Promise<void>((resolve, reject) => {
    out.toBlob((blob) => {
      if (!blob) {
        reject(new Error('toBlob returned null'));
        return;
      }
      const safeName = (projectName || 'scene').replace(/[^a-z0-9-_]+/gi, '_');
      const filename = opts.filename ?? `${safeName}-${nowTimestampForFilename()}.png`;
      triggerDownload(blob, filename);
      resolve();
    }, 'image/png');
  });
}
